import type {
  QuestionnaireItemAnswerOptionWithTranslations,
  QuestionnaireItemWithTranslations,
  QuestionnaireWithTranslations,
} from '@liza/fhir-utils';
import { LOINC_CODES } from '@liza/fhir-utils';
import {
  buildScoreObservationTemplate,
  buildTemplateExtractExtension,
  frequencyAnswerOption,
  type ItemFraming,
  SEMVER_VERSION_ALGORITHM_EXTENSION,
  symptomItem,
  withEs,
} from './scoredSurvey';

/**
 * PHQ-9 (Patient Health Questionnaire-9) depression screening instrument — the nine core scored
 * symptom items. Item structure and LOINC coding follow the HL7 SDC PHQ-9
 * (http://hl7.org/fhir/us/core/Questionnaire/phq-9-example), published here under our own canonical
 * `url` and version. Matched on the standard OID identifier, so re-deploys upsert rather than duplicate.
 *
 * All nine items are required and share the standard 0–3 frequency scale ({@link frequencyAnswerOptions}).
 * The supplementary functional-difficulty question (Q10, LOINC 69722-7) is included as an unscored optional item.
 *
 * The total score is derived server-side via Medplum's SDC `QuestionnaireResponse/$extract`:
 * a contained Observation template (see {@link buildScoreObservationTemplate}) carrying the
 * total-score LOINC plus standard `sdc-questionnaire-templateExtract` annotations. Behavior is
 * verified by `src/__tests__/liveMedplum/phq9ExtractScoring.test.ts`.
 *
 * Source: the official bilingual PHQ-9 (English/Spanish).
 * https://med.stanford.edu/content/dam/sm/ppc/documents/DBP/PHQ-9_bilingual.pdf
 * The English item text and framing statement (`Questionnaire.description`), and the inline Spanish
 * translations on the `_`-sibling elements (FHIR translation extension) — including the answer
 * options — are all transcribed verbatim from that PDF. Because PHQ-9 is a validated instrument,
 * these strings must be copied exactly and never free-translated; scoring validity depends on the
 * exact wording. Per the PDF: "Developed by Drs. Robert L. Spitzer, Janet B.W. Williams, Kurt
 * Kroenke and colleagues, with an educational grant from Pfizer Inc. No permission required to
 * reproduce, translate, display or distribute."
 */

/** PHQ-9 total score. Distinct from the panel LOINC (44249-1) on `Questionnaire.code`. */
export const PHQ9_TOTAL_SCORE_LOINC = LOINC_CODES.PHQ9_TOTAL_SCORE;

/**
 * `linkId` of item 9 — the suicidal-ideation item ("Thoughts that you would be better off dead or of
 * hurting yourself in some way"). Exported because a positive answer raises a care-team task
 * (`medplum-hooks`' Observation handler), which has to find the item on a response without knowing
 * which PHQ-9 version produced it. Stable across every deployed version, so the match needs no
 * version pin.
 */
export const PHQ9_ITEM_9_LINK_ID = '/44260-8';

/**
 * Bilingual "Over the last 2 weeks…" framing for Q1–Q9, passed to {@link symptomItem}. Q10
 * (functional difficulty, different format) omits framing by passing none.
 */
const phq9Framing: ItemFraming = {
  en: 'Over the last 2 weeks, how often have you been bothered by any of the following problems?',
  es: 'Durante las últimas 2 semanas, ¿qué tan seguido le han afectado cualquiera de los siguientes problemas?',
};

const frequencyAnswerOptions: QuestionnaireItemAnswerOptionWithTranslations[] = [
  frequencyAnswerOption('0', 0, 'LA6568-5', 'Not at all', 'Para nada'),
  frequencyAnswerOption('1', 1, 'LA6569-3', 'Several days', 'Varios días'),
  frequencyAnswerOption('2', 2, 'LA6570-1', 'More than half the days', 'Más de la mitad de los días'),
  frequencyAnswerOption('3', 3, 'LA6571-9', 'Nearly every day', 'Casi todos los días'),
];

const scoredSymptomItems: QuestionnaireItemWithTranslations[] = [
  symptomItem(
    '/44250-9',
    '44250-9',
    'Little interest or pleasure in doing things',
    'Little interest or pleasure in doing things',
    'Poco interés o placer en hacer las cosas',
    true,
    frequencyAnswerOptions,
    phq9Framing,
  ),
  symptomItem(
    '/44255-8',
    '44255-8',
    'Feeling down, depressed, or hopeless',
    'Feeling down, depressed, or hopeless',
    'Se ha sentido decaído(a), deprimido(a), o sin esperanzas',
    true,
    frequencyAnswerOptions,
    phq9Framing,
  ),
  symptomItem(
    '/44259-0',
    '44259-0',
    'Trouble falling or staying asleep, or sleeping too much',
    'Trouble falling or staying asleep, or sleeping too much',
    'Dificultad para dormir o permanecer dormido(a), o ha dormido demasiado',
    true,
    frequencyAnswerOptions,
    phq9Framing,
  ),
  symptomItem(
    '/44254-1',
    '44254-1',
    'Feeling tired or having little energy',
    'Feeling tired or having little energy',
    'Se ha sentido cansado(a) o con poca energía',
    true,
    frequencyAnswerOptions,
    phq9Framing,
  ),
  symptomItem(
    '/44251-7',
    '44251-7',
    'Poor appetite or overeating',
    'Poor appetite or overeating',
    'Con poco apetito o ha comido en exceso',
    true,
    frequencyAnswerOptions,
    phq9Framing,
  ),
  symptomItem(
    '/44258-2',
    '44258-2',
    'Feeling bad about yourself — or that you are a failure or have let yourself or your family down',
    'Feeling bad about yourself — or that you are a failure or have let yourself or your family down',
    'Se ha sentido mal con usted mismo(a) – o que es un fracaso o que ha quedado mal con usted mismo(a) o con su familia',
    true,
    frequencyAnswerOptions,
    phq9Framing,
  ),
  symptomItem(
    '/44252-5',
    '44252-5',
    'Trouble concentrating on things, such as reading the newspaper or watching television',
    'Trouble concentrating on things, such as reading the newspaper or watching television',
    'Ha tenido dificultad para concentrarse en cosas tales como leer el periódico o ver televisión',
    true,
    frequencyAnswerOptions,
    phq9Framing,
  ),
  symptomItem(
    '/44253-3',
    '44253-3',
    'Moving or speaking so slowly that other people could have noticed? Or the opposite — being so fidgety or restless that you have been moving around a lot more than usual',
    'Moving or speaking so slowly that other people could have noticed? Or the opposite — being so fidgety or restless that you have been moving around a lot more than usual',
    '¿Se ha estado moviendo o hablando tan lento que otras personas podrían notarlo?, o por el contrario – ha estado tan inquieto(a) o agitado(a), que se ha estado moviendo mucho más de lo normal',
    true,
    frequencyAnswerOptions,
    phq9Framing,
  ),
  symptomItem(
    PHQ9_ITEM_9_LINK_ID,
    '44260-8',
    'Thoughts that you would be better off dead or of hurting yourself in some way',
    'Thoughts that you would be better off dead or of hurting yourself in some way',
    'Ha pensado que estaría mejor muerto(a) o se le ha ocurrido lastimarse de alguna manera',
    true,
    frequencyAnswerOptions,
    phq9Framing,
  ),
];

/**
 * Q10 — functional difficulty question. Not scored (all ordinalValues = 0); not in scoredSymptomItems.
 * No framing extension → no "Over the last 2 weeks…" prompt shown above it.
 * LOINC: 69722-7.
 */
const q10FunctionalDifficulty: QuestionnaireItemWithTranslations = symptomItem(
  '/69722-7',
  '69722-7',
  'If you checked off any problems, how difficult have these problems made it for you to do your work, take care of things at home, or get along with other people?',
  'If you checked off any problems, how difficult have these problems made it for you to do your work, take care of things at home, or get along with other people?',
  'Si usted marcó cualquiera de estos problemas, ¿qué tan difícil fue hacer su trabajo, las tareas del hogar o llevarse bien con otras personas debido a tales problemas?',
  false,
  [
    frequencyAnswerOption('0', 0, 'LA6572-7', 'Not difficult at all', 'Para nada difícil'),
    frequencyAnswerOption('0', 0, 'LA6573-5', 'Somewhat difficult', 'Un poco difícil'),
    frequencyAnswerOption('0', 0, 'LA6575-0', 'Very difficult', 'Muy difícil'),
    frequencyAnswerOption('0', 0, 'LA6574-3', 'Extremely difficult', 'Extremadamente difícil'),
  ],
);

const phq9ScoreObservationTemplate = buildScoreObservationTemplate({
  id: 'phq9-score-tmpl',
  totalScoreCode: PHQ9_TOTAL_SCORE_LOINC,
  display: 'PHQ-9',
  scoredItems: scoredSymptomItems,
});

export const phq9Questionnaire: QuestionnaireWithTranslations = {
  resourceType: 'Questionnaire',
  meta: {
    profile: ['http://hl7.org/fhir/uv/sdc/StructureDefinition/sdc-questionnaire'],
  },
  extension: [SEMVER_VERSION_ALGORITHM_EXTENSION, buildTemplateExtractExtension('phq9-score-tmpl')],
  contained: [phq9ScoreObservationTemplate],
  url: 'https://liza.health/fhir/questionnaire/phq-9',
  identifier: [{ system: 'urn:ietf:rfc:3986', value: 'urn:oid:2.16.840.1.113883.4.642.40.2.35.5' }],
  // 2: added SDC $extract scoring annotations (contained Observation template)
  // 3: added the framing statement (`description`) and trimmed "in the last 2 weeks" from each item's
  //    text — the timeframe now lives in the framing statement shown above every question.
  // 4: added Q10 functional-difficulty question (LOINC 69722-7, unscored); moved framing from
  //    `description` to per-item extension so Q10 shows no framing.
  version: '4',
  name: 'PHQ9',
  language: 'en',
  title: 'PHQ-9',
  _title: withEs('Cuestionario sobre la salud del paciente-9 (PHQ-9)'),
  status: 'active',
  subjectType: ['Patient'],
  date: '2026-06-10',
  publisher: 'Liza Health',
  jurisdiction: [{ coding: [{ system: 'urn:iso:std:iso:3166', code: 'US' }] }],
  copyright:
    'This material contains content from LOINC® (http://loinc.org). LOINC is copyright © 1995-2024, Regenstrief Institute, Inc. and the LOINC Committee, and is available at no cost under the license at http://loinc.org/license. Copyright © Pfizer Inc. All rights reserved. Developed by Drs. Robert L. Spitzer, Janet B.W. Williams, Kurt Kroenke and colleagues, with an educational grant from Pfizer Inc. No permission required to reproduce, translate, display or distribute (https://www.phqscreeners.com/).',
  code: [{ system: 'http://loinc.org', code: '44249-1', display: 'PHQ-9 quick depression assessment panel' }],
  item: [...scoredSymptomItems, q10FunctionalDifficulty],
};

// Frozen prior version, retained verbatim. QuestionnaireResponses and Tasks created against v2 pin
// `...|2`, and `getByUrl` resolves by exact version — so v2 must stay deployed for those responses to
// keep rendering the wording the patient actually saw (v2 carried the timeframe inline; v3 moved it to
// the framing statement). `status: 'retired'` keeps it resolvable but the survey picker
// (`SURVEY_QUESTIONNAIRES`) offers only the active version. Don't edit these strings — repinning old
// responses to v3 would misrepresent what was presented. See the survey FHIR design-decision doc.
//
// Unlike the active v3 items, these strings do NOT match the source PDF verbatim: v2 carried the
// timeframe inline ("in the last 2 weeks?"), which the PDF does not, and the Spanish translations
// are left as placeholders. They are preserved as the exact wording v2 respondents saw, not as a
// transcription of the validated bilingual PDF.
const phq9V2SymptomItems: QuestionnaireItemWithTranslations[] = [
  symptomItem(
    '/44250-9',
    '44250-9',
    'Little interest or pleasure in doing things in the last 2 weeks?',
    'Little interest or pleasure in doing things in the last 2 weeks?',
    '__PLACEHOLDER__',
    true,
    frequencyAnswerOptions,
  ),
  symptomItem(
    '/44255-8',
    '44255-8',
    'Feeling down, depressed, or hopeless in the last 2 weeks?',
    'Feeling down, depressed, or hopeless in the last 2 weeks?',
    '__PLACEHOLDER__',
    true,
    frequencyAnswerOptions,
  ),
  symptomItem(
    '/44259-0',
    '44259-0',
    'Trouble falling or staying asleep, or sleeping too much in the last 2 weeks?',
    'Trouble falling or staying asleep, or sleeping too much in the last 2 weeks?',
    '__PLACEHOLDER__',
    true,
    frequencyAnswerOptions,
  ),
  symptomItem(
    '/44254-1',
    '44254-1',
    'Feeling tired or having little energy in the last 2 weeks?',
    'Feeling tired or having little energy in the last 2 weeks?',
    '__PLACEHOLDER__',
    true,
    frequencyAnswerOptions,
  ),
  symptomItem(
    '/44251-7',
    '44251-7',
    'Poor appetite or overeating in the last 2 weeks?',
    'Poor appetite or overeating in the last 2 weeks?',
    '__PLACEHOLDER__',
    true,
    frequencyAnswerOptions,
  ),
  symptomItem(
    '/44258-2',
    '44258-2',
    'Feeling bad about yourself - or that you are a failure or have let yourself or your family down - in the last 2 weeks?',
    'Feeling bad about yourself — or that you are a failure or have let yourself or your family down — in the last 2 weeks?',
    '__PLACEHOLDER__',
    true,
    frequencyAnswerOptions,
  ),
  symptomItem(
    '/44252-5',
    '44252-5',
    'Trouble concentrating on things, such as reading the newspaper or watching television, in the last 2 weeks?',
    'Trouble concentrating on things, such as reading the newspaper or watching television, in the last 2 weeks?',
    '__PLACEHOLDER__',
    true,
    frequencyAnswerOptions,
  ),
  symptomItem(
    '/44253-3',
    '44253-3',
    'Moving or speaking so slowly that other people could have noticed. Or the opposite - being so fidgety or restless that you have been moving around a lot more than usual - in the last 2 weeks?',
    'Moving or speaking so slowly that other people could have noticed, or the opposite — being so fidgety or restless that you have been moving around a lot more than usual — in the last 2 weeks?',
    '__PLACEHOLDER__',
    true,
    frequencyAnswerOptions,
  ),
  symptomItem(
    PHQ9_ITEM_9_LINK_ID,
    '44260-8',
    'Thoughts that you would be better off dead, or of hurting yourself in some way, in the last 2 weeks?',
    'Thoughts that you would be better off dead, or of hurting yourself in some way, in the last 2 weeks?',
    '__PLACEHOLDER__',
    true,
    frequencyAnswerOptions,
  ),
];

export const phq9QuestionnaireV2: QuestionnaireWithTranslations = {
  ...phq9Questionnaire,
  version: '2',
  status: 'retired',
  description: undefined,
  _description: undefined,
  item: phq9V2SymptomItems,
};

// Frozen v3 definition. QuestionnaireResponses created against v3 pin `...|3`. v3 introduced the
// framing statement (`description`) and trimmed "in the last 2 weeks" from each item's text.
// The items are the 9 scored symptom items without per-item framing extensions (those were added
// in v4). Spanish translations are verbatim from the Stanford bilingual PDF.
// Don't edit these strings — they represent the exact wording v3 respondents saw.
const phq9V3SymptomItems: QuestionnaireItemWithTranslations[] = [
  symptomItem(
    '/44250-9',
    '44250-9',
    'Little interest or pleasure in doing things',
    'Little interest or pleasure in doing things',
    'Poco interés o placer en hacer las cosas',
    true,
    frequencyAnswerOptions,
  ),
  symptomItem(
    '/44255-8',
    '44255-8',
    'Feeling down, depressed, or hopeless',
    'Feeling down, depressed, or hopeless',
    'Se ha sentido decaído(a), deprimido(a), o sin esperanzas',
    true,
    frequencyAnswerOptions,
  ),
  symptomItem(
    '/44259-0',
    '44259-0',
    'Trouble falling or staying asleep, or sleeping too much',
    'Trouble falling or staying asleep, or sleeping too much',
    'Dificultad para dormir o permanecer dormido(a), o ha dormido demasiado',
    true,
    frequencyAnswerOptions,
  ),
  symptomItem(
    '/44254-1',
    '44254-1',
    'Feeling tired or having little energy',
    'Feeling tired or having little energy',
    'Se ha sentido cansado(a) o con poca energía',
    true,
    frequencyAnswerOptions,
  ),
  symptomItem(
    '/44251-7',
    '44251-7',
    'Poor appetite or overeating',
    'Poor appetite or overeating',
    'Con poco apetito o ha comido en exceso',
    true,
    frequencyAnswerOptions,
  ),
  symptomItem(
    '/44258-2',
    '44258-2',
    'Feeling bad about yourself — or that you are a failure or have let yourself or your family down',
    'Feeling bad about yourself — or that you are a failure or have let yourself or your family down',
    'Se ha sentido mal con usted mismo(a) – o que es un fracaso o que ha quedado mal con usted mismo(a) o con su familia',
    true,
    frequencyAnswerOptions,
  ),
  symptomItem(
    '/44252-5',
    '44252-5',
    'Trouble concentrating on things, such as reading the newspaper or watching television',
    'Trouble concentrating on things, such as reading the newspaper or watching television',
    'Ha tenido dificultad para concentrarse en cosas tales como leer el periódico o ver televisión',
    true,
    frequencyAnswerOptions,
  ),
  symptomItem(
    '/44253-3',
    '44253-3',
    'Moving or speaking so slowly that other people could have noticed? Or the opposite — being so fidgety or restless that you have been moving around a lot more than usual',
    'Moving or speaking so slowly that other people could have noticed? Or the opposite — being so fidgety or restless that you have been moving around a lot more than usual',
    '¿Se ha estado moviendo o hablando tan lento que otras personas podrían notarlo?, o por el contrario – ha estado tan inquieto(a) o agitado(a), que se ha estado moviendo mucho más de lo normal',
    true,
    frequencyAnswerOptions,
  ),
  symptomItem(
    PHQ9_ITEM_9_LINK_ID,
    '44260-8',
    'Thoughts that you would be better off dead or of hurting yourself in some way',
    'Thoughts that you would be better off dead or of hurting yourself in some way',
    'Ha pensado que estaría mejor muerto(a) o se le ha ocurrido lastimarse de alguna manera',
    true,
    frequencyAnswerOptions,
  ),
];

export const phq9QuestionnaireV3: QuestionnaireWithTranslations = {
  ...phq9Questionnaire,
  version: '3',
  status: 'retired',
  description: 'Over the last 2 weeks, how often have you been bothered by any of the following problems?',
  _description: withEs(
    'Durante las últimas 2 semanas, ¿qué tan seguido le han afectado cualquiera de los siguientes problemas?',
  ),
  item: phq9V3SymptomItems,
};
